import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import {
  ApiError,
  createInvoice,
  getBilling,
  moveInvoice,
  recordReceipt,
  type BillingView,
  type Invoice,
  type InvoiceStanding,
} from '../api.js';
import { formatDate, formatMoney, moneyForInput } from '../labels.js';
import {
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';

/**
 * Billing and receivables (CM-05/CM-06) — the last screen of the commercial
 * module.
 *
 * It answers one question: **are we being paid on this job?** The cost report
 * says what a project spends; this says what it collects, and the gap is
 * whether anybody gets paid this month.
 *
 * WHAT IS NOT STORED
 * ------------------
 * Whether an invoice is paid, part-paid or overdue is worked out from the
 * receipts against it and today's date — never stored. A stored "overdue" is
 * wrong the moment a payment lands or a day passes and nobody recomputes it,
 * which is the same reasoning as milestone status.
 *
 * The LIFECYCLE — draft, issued, cancelled — is stored, because somebody
 * decides it.
 */

const STANDING_LABELS: Readonly<Record<InvoiceStanding, string>> = {
  draft: 'Draft, not sent',
  cancelled: 'Cancelled',
  paid: 'Paid',
  part_paid: 'Part paid',
  due: 'Awaiting payment',
  overdue: 'Overdue',
};

/** Never colour alone — the symbol and the word both carry the state. */
const STANDING_SYMBOLS: Readonly<Record<InvoiceStanding, string>> = {
  draft: '○',
  cancelled: '—',
  paid: '✓',
  part_paid: '◐',
  due: '◔',
  overdue: '■',
};

interface Props {
  readonly projectId: string;
  readonly currency: string;
  readonly can: { readonly write: boolean; readonly edit: boolean };
  /** An operator support visit that did not ask for the money (ADR-041). */
  readonly visiting?: boolean;
}

export function Billing({ projectId, currency, can, visiting = false }: Props): JSX.Element {
  const [view, setView] = useState<BillingView | null>(null);
  /** Not the same as having raised no invoices — see `Drawings.tsx`. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  // `register`, not `view` — this screen already calls its data `view`.
  const register = useRegisterView('due');

  const load = useCallback(async () => {
    try {
      setView(await getBilling(projectId));
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the invoices.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The invoices on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps`. It runs before the early
   * returns below because hooks must.
   */
  const shown = useMemo(() => {
    const invoices = view?.invoices;
    if (!invoices) return [];

    const standing = register.chosen.standing;

    const filtered = invoices.filter((invoice) => {
      const hay = [invoice.reference, invoice.description].filter(Boolean).join(' ');
      if (!matches(hay, register.query)) return false;
      // "Owed" is the question this screen exists to answer, so it is one
      // choice rather than three the reader has to add up.
      if (standing === 'owed') {
        return invoice.standing === 'due' || invoice.standing === 'overdue' ||
          invoice.standing === 'part_paid';
      }
      if (standing && invoice.standing !== standing) return false;
      return true;
    });

    const byDue = (a: Invoice, b: Invoice): number => {
      const x = a.due_date;
      const y = b.due_date;
      if (x === y) return 0;
      if (!x) return 1;
      if (!y) return -1;
      return x < y ? -1 : 1;
    };

    const compare: Readonly<Record<string, (a: Invoice, b: Invoice) => number>> = {
      due: byDue,
      // Worst first — the point of the screen is what is not being paid.
      overdue: (a, b) => (b.days_overdue ?? -1) - (a.days_overdue ?? -1),
      outstanding: (a, b) => Number(b.outstanding) - Number(a.outstanding),
      reference: (a, b) => a.reference.localeCompare(b.reference),
    };

    return [...filtered].sort(compare[register.sortKey] ?? compare.due!);
  }, [view, register.query, register.chosen.standing, register.sortKey]);

  async function run(work: () => Promise<BillingView>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setView(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Billing</h3>
      {view && can.write && !adding && (
        <button type="button" className="inline" onClick={() => setAdding(true)}>
          Raise an invoice
        </button>
      )}
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">
          {visiting
            ? 'This visit cannot see money, so the invoices are not shown.'
            : 'You do not have financial access on this project, so the invoices are not shown.'}
        </p>
      </>
    );
  }

  if (!view) {
    return (
      <>
        {head}
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Loading the invoices…</p>
        )}
      </>
    );
  }

  const { receivables } = view;

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {/*
        Said before the table, because it is the sentence somebody repeats in a
        cashflow meeting. Shown even at zero — a banner that only appears when
        things are bad teaches people its absence means nobody looked.
      */}
      {view.invoices.length > 0 && (
        <div
          className={`panel exposure exposure-${
            receivables.worst_days_overdue > 60
              ? 'red'
              : receivables.worst_days_overdue > 30
                ? 'amber'
                : 'green'
          }`}
          role="status"
        >
          {receivables.overdue_count > 0 ? (
            <p>
              <strong>
                {currency} {formatMoney(receivables.overdue)} is overdue
              </strong>{' '}
              across {receivables.overdue_count}{' '}
              {receivables.overdue_count === 1 ? 'invoice' : 'invoices'} — the oldest by{' '}
              {receivables.worst_days_overdue} days.
            </p>
          ) : (
            <p>
              <strong>Nothing is overdue.</strong> {currency}{' '}
              {formatMoney(receivables.outstanding)} is outstanding and within terms.
            </p>
          )}
          <p className="hint">
            {currency} {formatMoney(receivables.invoiced)} invoiced, {formatMoney(receivables.received)}{' '}
            received.
          </p>
        </div>
      )}

      {adding && (
        <InvoiceForm
          currency={currency}
          busy={busy}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => createInvoice(projectId, input))) setAdding(false);
          }}
        />
      )}

      {paying && (
        <ReceiptForm
          invoice={view.invoices.find((i) => i.id === paying)!}
          currency={currency}
          busy={busy}
          onCancel={() => {
            setPaying(null);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => recordReceipt(projectId, paying, input))) setPaying(null);
          }}
        />
      )}

      {cancelling && (
        <CancelForm
          invoice={view.invoices.find((i) => i.id === cancelling)!}
          busy={busy}
          onCancel={() => {
            setCancelling(null);
            setError(null);
          }}
          onSubmit={async (reason) => {
            const ok = await run(() =>
              moveInvoice(projectId, cancelling, { status: 'cancelled', reason }),
            );
            if (ok) setCancelling(null);
          }}
        />
      )}

      {view.invoices.length === 0 ? (
        <div className="empty">
          <p>
            <strong>Nothing has been invoiced yet.</strong>
          </p>
          <p>
            {can.write
              ? 'Raise an application or an invoice and record the payments against it. What is overdue follows from the dates.'
              : /*
                 * NAME THE ROLE, as the variations register does. An empty
                 * state must name an action ITS READER can take — or, when
                 * they cannot take one, say who can.
                 *
                 * `billing: VCEDX` is the QS alone. A Company Admin and a
                 * Director hold `VX`: they read this screen and export it and
                 * raise nothing on it. The old sentence left them hunting for
                 * a button that was never going to be there.
                 */
                'Nobody has raised an invoice on this project. The QS raises the applications and invoices, and records the payments against them.'}
          </p>
        </div>
      ) : (
        <>
        <RegisterToolbar
          view={register}
          searchLabel="Search invoices"
          searchPlaceholder="Reference or description"
          shown={shown.length}
          total={view.invoices.length}
          noun={['invoice', 'invoices']}
          filters={[
            {
              key: 'standing',
              label: 'Show',
              options: [
                { value: 'owed', label: 'Still owed' },
                { value: 'overdue', label: 'Overdue' },
                { value: 'paid', label: 'Paid' },
                { value: 'draft', label: 'Draft, not sent' },
                { value: 'cancelled', label: 'Cancelled' },
              ],
            },
          ]}
          sorts={[
            { key: 'due', label: 'Due date' },
            { key: 'overdue', label: 'Most overdue' },
            { key: 'outstanding', label: 'Largest outstanding' },
            { key: 'reference', label: 'Reference' },
          ]}
        />

        {shown.length === 0 ? (
          /*
           * A DIFFERENT empty state from "nothing invoiced yet": this reader
           * has invoices and a filter that excludes all of them.
           */
          <div className="empty">
            <p>
              <strong>No invoice matches that.</strong>
            </p>
            <p>
              <button type="button" className="link-button" onClick={register.clear}>
                Clear the search and filters
              </button>{' '}
              to see all {view.invoices.length}.
            </p>
          </div>
        ) : (
        <div className={`rk-scroll${register.density === 'compact' ? ' rk-compact' : ''}`}>
        <table className="grid rk-cards">
          <caption className="visually-hidden">
            Invoices, what is outstanding and how late they are
          </caption>
          <thead>
            <tr>
              <th scope="col">Invoice</th>
              <th scope="col">Gross ({currency})</th>
              <th scope="col">Received</th>
              <th scope="col">Outstanding</th>
              <th scope="col">Due</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((invoice) => (
              <tr
                key={invoice.id}
                className={invoice.standing === 'overdue' ? 'vo-exposed' : undefined}
              >
                <th scope="row">
                  <span className="ms-symbol" aria-hidden="true">
                    {STANDING_SYMBOLS[invoice.standing]}
                  </span>{' '}
                  {invoice.reference}
                  <span className="tree-code"> {STANDING_LABELS[invoice.standing]}</span>
                  {invoice.description && (
                    <span className="snag-note">{invoice.description}</span>
                  )}
                  {invoice.cancelled_reason && (
                    <span className="snag-note">Cancelled: {invoice.cancelled_reason}</span>
                  )}
                  {invoice.receipts.map((receipt) => (
                    <span className="snag-check" key={receipt.id}>
                      {formatMoney(receipt.amount)} received {formatDate(receipt.received_on)}
                      {receipt.reference ? ` · ${receipt.reference}` : ''}
                    </span>
                  ))}
                </th>
                <td data-label="Gross">{formatMoney(invoice.gross)}</td>
                <td data-label="Received">{formatMoney(invoice.received)}</td>
                <td data-label="Outstanding">{formatMoney(invoice.outstanding)}</td>
                <td data-label="Due">
                  {formatDate(invoice.due_date)}
                  {/* In words, on the row. A colour alone would not survive a
                      printout, and this is the line somebody reads down. */}
                  {invoice.days_overdue !== null && (
                    <span className="task-overdue">
                      {invoice.days_overdue} {invoice.days_overdue === 1 ? 'day' : 'days'} late
                    </span>
                  )}
                </td>
                <td>
                  {/*
                    The invoice's own life, forwards: issue it, then record what
                    comes in against it. Cancelling is an ending that needs a
                    reason, so it stays in the menu.
                  */}
                  <RowActions
                    rowLabel={invoice.reference}
                    {...(() => {
                      const primary: RowAction | null =
                        can.edit && invoice.status === 'draft'
                          ? {
                              label: 'Issue it',
                              disabled: busy,
                              onSelect: () =>
                                void run(() =>
                                  moveInvoice(projectId, invoice.id, { status: 'issued' }),
                                ),
                            }
                          : can.write && invoice.status === 'issued' && invoice.standing !== 'paid'
                            ? {
                                label: 'Record a payment',
                                disabled: busy,
                                onSelect: () => {
                                  setError(null);
                                  setPaying(invoice.id);
                                },
                              }
                            : null;
                      return primary ? { primary } : {};
                    })()}
                    more={
                      can.edit && invoice.status !== 'cancelled' && invoice.receipts.length === 0
                        ? [
                            {
                              label: 'Cancel',
                              danger: true,
                              disabled: busy,
                              onSelect: () => {
                                setError(null);
                                setCancelling(invoice.id);
                              },
                            } satisfies RowAction,
                          ]
                        : []
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        )}
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

const MONEY_OK = /^\d+(\.\d{1,4})?$/;
const today = () => new Date().toISOString().slice(0, 10);

function InvoiceForm({
  currency,
  busy,
  onCancel,
  onSubmit,
}: {
  readonly currency: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    reference: string;
    description?: string;
    invoice_date: string;
    due_date: string;
    amount: string;
    tax_amount: string;
    status: 'draft' | 'issued';
  }) => void | Promise<void>;
}): JSX.Element {
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState(() =>
    new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10),
  );
  const [amount, setAmount] = useState('');
  const [tax, setTax] = useState('');
  const [status, setStatus] = useState<'draft' | 'issued'>('issued');

  const money = (v: string) => v.replace(/,/g, '').trim();
  const valid =
    reference.trim().length > 0 &&
    MONEY_OK.test(money(amount)) &&
    Number(money(amount)) > 0 &&
    (tax.trim() === '' || MONEY_OK.test(money(tax))) &&
    dueDate >= invoiceDate;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!valid) return;
    void onSubmit({
      reference: reference.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      invoice_date: invoiceDate,
      due_date: dueDate,
      amount: money(amount),
      tax_amount: money(tax) || '0.0000',
      status,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Raise an invoice</p>

      <div className="field-row">
        <div className="field">
          <label htmlFor="in-reference">Invoice number</label>
          <input
            id="in-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={120}
            disabled={busy}
            placeholder="INV-001"
          />
          <p className="hint">This is what gets quoted when you chase it.</p>
        </div>
        <div className="field">
          <label htmlFor="in-status">Issue it now?</label>
          <select
            id="in-status"
            value={status}
            disabled={busy}
            onChange={(e) => setStatus(e.target.value as 'draft' | 'issued')}
          >
            <option value="issued">Yes, it has gone out</option>
            <option value="draft">No, keep it as a draft</option>
          </select>
          <p className="hint">A draft is never counted as owed and can never be overdue.</p>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="in-amount">Amount before tax ({currency})</label>
          <input
            id="in-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            disabled={busy}
            placeholder="100000.00"
          />
        </div>
        <div className="field">
          <label htmlFor="in-tax">Tax ({currency})</label>
          <input
            id="in-tax"
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            inputMode="decimal"
            disabled={busy}
            placeholder="5000.00"
          />
          <p className="hint">What the client owes is the two added together.</p>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="in-date">Invoice date</label>
          <input
            id="in-date"
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label htmlFor="in-due">Due</label>
          <input
            id="in-due"
            type="date"
            value={dueDate}
            min={invoiceDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={busy}
          />
          {dueDate < invoiceDate && (
            <p className="hint">
              <strong>The due date is before the invoice date.</strong>
            </p>
          )}
        </div>
      </div>

      <div className="field">
        <label htmlFor="in-description">What it is for</label>
        <input
          id="in-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={4000}
          disabled={busy}
          placeholder="Application 3 — works to 31 July"
        />
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !valid}>
          {busy ? 'Saving…' : 'Raise it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ReceiptForm({
  invoice,
  currency,
  busy,
  onCancel,
  onSubmit,
}: {
  readonly invoice: Invoice;
  readonly currency: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    amount: string;
    received_on: string;
    reference?: string;
  }) => void | Promise<void>;
}): JSX.Element {
  // Defaulted to what is left, because a full payment is the common case and
  // retyping a figure already on the screen is how it gets typed wrong.
  // The stored value is four decimals; the person is about to edit it.
  const [amount, setAmount] = useState(moneyForInput(invoice.outstanding));
  const [on, setOn] = useState(today());
  const [reference, setReference] = useState('');

  const money = amount.replace(/,/g, '').trim();
  const valid = MONEY_OK.test(money) && Number(money) > 0;

  return (
    <div className="panel">
      <p className="panel-title">Record a payment against {invoice.reference}</p>
      <p className="hint">
        {currency} {formatMoney(invoice.outstanding)} outstanding of {formatMoney(invoice.gross)}.
      </p>

      <div className="field-row">
        <div className="field">
          <label htmlFor="rc-amount">Amount received ({currency})</label>
          <input
            id="rc-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            disabled={busy}
          />
        </div>
        <div className="field">
          <label htmlFor="rc-on">Received on</label>
          <input
            id="rc-on"
            type="date"
            value={on}
            onChange={(e) => setOn(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="rc-reference">Transfer reference</label>
        <input
          id="rc-reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          maxLength={120}
          disabled={busy}
          placeholder="TT-9981"
        />
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !valid}
          onClick={() =>
            void onSubmit({
              amount: money,
              received_on: on,
              ...(reference.trim() ? { reference: reference.trim() } : {}),
            })
          }
        >
          {busy ? 'Saving…' : 'Record it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const MIN_REASON = 20;

function CancelForm({
  invoice,
  busy,
  onCancel,
  onSubmit,
}: {
  readonly invoice: Invoice;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (reason: string) => void | Promise<void>;
}): JSX.Element {
  const [reason, setReason] = useState('');

  return (
    <div className="panel">
      <p className="panel-title">Cancel {invoice.reference}</p>
      <p className="hint">
        A cancelled invoice stops being owed and keeps its number. Somebody will ask why months
        from now, so the record has to say. If money has already come in against it, raise a credit
        note instead — cancelling would make this disagree with the bank.
      </p>

      <div className="field">
        <label htmlFor="cn-reason">Why?</label>
        <textarea
          id="cn-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={2000}
          disabled={busy}
          placeholder="Raised against the wrong project by mistake."
        />
        <p className="hint">
          {reason.trim().length}/{MIN_REASON} characters minimum.
        </p>
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || reason.trim().length < MIN_REASON}
          onClick={() => void onSubmit(reason.trim())}
        >
          {busy ? 'Saving…' : 'Cancel the invoice'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Keep it
        </button>
      </div>
    </div>
  );
}
